import React, { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Crown, Cloud, Terminal, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSeedPool, loadInsights, pickInsight } from '@/lib/insightsSource';

const ValueCard = ({ tier, title, desc, icon: Icon, meta, pool }) => {
  // First paint uses the seed pool synchronously so there is never a
  // loading spinner or fallback message. The parent upgrades `pool`
  // once the async sheet fetch resolves.
  const [insight, setInsight] = useState(() => pickInsight(getSeedPool(), tier));
  const [isExpanded, setIsExpanded] = useState(false);

  // When the parent swaps in a fresher pool (sheet or cached), pick a
  // new insight for this tier card. Only swap if the pool reference
  // actually changes — avoids churn on every render.
  useEffect(() => {
    if (!pool) return;
    const next = pickInsight(pool, tier);
    if (next) setInsight(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool]);

  const handleMouseEnter = () => setIsExpanded(true);
  const handleMouseLeave = () => setIsExpanded(false);
  const handleClick = () => setIsExpanded((v) => !v);

  return (
    <motion.div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      className="relative pl-8 md:pl-12 py-4 cursor-pointer"
    >
      {/* Connector Node */}
      <div className={cn(
        "absolute left-[-5px] top-8 w-3 h-3 rounded-full border-2 bg-[#1A1D21] transition-colors duration-300 z-10",
        isExpanded ? "border-[#4A90E2] bg-[#4A90E2]" : "border-gray-600"
      )} />

      <div className={cn(
        "relative p-6 md:p-8 transition-all duration-300 border rounded-[2px] overflow-hidden backdrop-blur-md",
        isExpanded
          ? "bg-white/[0.08] border-[#4A90E2]/60 shadow-[0_0_30px_rgba(74,144,226,0.2)]"
          : "bg-white/[0.04] border-white/[0.09] hover:bg-white/[0.06] hover:border-white/20"
      )}>
        {/* Corner glow blob */}
        <div className={cn(
          "absolute bottom-[-28px] right-[-28px] w-36 h-36 rounded-full blur-3xl transition-opacity duration-400 pointer-events-none",
          isExpanded ? "opacity-20 bg-[#4A90E2]" : "opacity-[0.05] bg-[#4A90E2]"
        )} />

        {/* Monospace Meta-Accent */}
        <div className="font-mono text-[10px] text-gray-500 mb-2 tracking-widest uppercase">
          {meta}
        </div>

        <div className="flex items-start gap-4">
          <div className="mt-1 p-2 bg-white/[0.07] rounded-[2px]">
            <Icon className={cn("w-6 h-6 transition-colors", isExpanded ? "text-[#4A90E2]" : "text-gray-300")} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-xl mb-2 text-white">{title}</h3>
            <p className="font-body text-gray-400 leading-[1.6] text-base">
              {desc}
            </p>
            {/* Tap hint for mobile */}
            {!isExpanded && (
              <span className="text-[10px] text-[#4A90E2] mt-2 block font-mono uppercase tracking-wider opacity-60">
                Tap for AI insight →
              </span>
            )}
          </div>
        </div>

        {/* AI Insight Overlay */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mt-4 pt-4 border-t border-dashed border-[#4A90E2]/30"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold text-[#4A90E2] uppercase tracking-wider">
                  AI Trend Analysis • 2026
                </span>
              </div>
              <p className="text-sm font-mono text-gray-200 italic">
                {insight}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default function ValueModel() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const lineHeight = useTransform(scrollYProgress, [0.1, 0.8], ["0%", "100%"]);

  // ------------------------------------------------------------------
  // Insights pool lifecycle.
  // 1. First paint: use the bundled seed (synchronous, always available).
  // 2. On mount: try to load a fresher pool (Google Sheet via the
  //    1-hour localStorage cache). On success we swap the pool, which
  //    causes the banner and the three tier cards to pick NEW insights
  //    from the merged pool. On failure, seed remains in place.
  // The UI never renders a loading spinner or fallback message.
  const [pool, setPool] = useState(() => getSeedPool());
  const [bannerInsight, setBannerInsight] = useState(() => pickInsight(getSeedPool(), "banner"));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await loadInsights(); // never rejects
      if (cancelled) return;
      setPool(loaded);
      const next = pickInsight(loaded, "banner");
      if (next) setBannerInsight(next);
    })();
    return () => { cancelled = true; };
  }, []);

  const tiers = [
    {
      tier: "CIO",
      meta: "01_STRAT",
      title: "THE CIO LEVEL",
      desc: "We conduct audits, define governance frameworks, and manage the 'Build vs. Buy' assessment.",
      icon: Crown
    },
    {
      tier: "CTO",
      meta: "02_ARCH",
      title: "THE CTO LEVEL",
      desc: "We design secure, scalable infrastructure on AWS and Google Cloud.",
      icon: Cloud
    },
    {
      tier: "OPS",
      meta: "03_EXEC // THE DELIVERY ENGINE",
      title: "THE DELIVERY LEVEL",
      desc: "Strategy fails without adoption. We provide the Technical Program Management and Organizational Change protocols required to steer complex deployments from concept to 'business-as-usual'.",
      icon: Terminal
    }
  ];

  return (
    <section ref={containerRef} className="py-24 bg-[#1A1D21] relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="mb-16 text-center max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-title text-white mb-4">Value Architecture</h2>
          <p className="text-gray-400 font-body text-lg">
            Connecting high-level strategy to low-level execution.
          </p>
        </div>

        {/* Live AI Trend Banner — seed-first, sheet-upgraded on mount. */}
        <div className="max-w-3xl mx-auto mb-12">
          <div
            className={cn(
              "relative border border-dashed rounded-[2px] px-5 py-4 md:px-6 md:py-5",
              "bg-white/[0.05] backdrop-blur-md transition-colors border-[#4A90E2]/35"
            )}
            data-testid="featured-ai-insight"
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-[#4A90E2]" />
              <span className="text-[10px] font-bold text-[#4A90E2] uppercase tracking-[0.2em]">
                // LIVE AI TREND ANALYSIS
              </span>
              <span className="ml-auto text-[9px] font-mono text-[#4A90E2]/70 uppercase tracking-widest">
                ● LIVE
              </span>
            </div>
            <p className="text-sm md:text-base font-mono text-gray-300 italic leading-relaxed">
              {bannerInsight}
            </p>
          </div>
        </div>

        <div className="relative max-w-3xl mx-auto">
          {/* Vertical Line Container */}
          <div className="absolute left-[3px] md:left-[3px] top-8 bottom-8 w-[2px] bg-white/10">
            <motion.div 
              style={{ height: lineHeight }} 
              className="w-full bg-[#4A90E2] origin-top"
            />
          </div>

          <div className="space-y-8">
            {tiers.map((t, i) => (
              <ValueCard key={i} {...t} pool={pool} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
